import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  SelectCardItem,
  SearchInput,
  FixedBottomButton,
  HeaderWithTitleAndDescription,
} from '@/components'
import { useGetCurrenciesCatalogs } from '@/domain/currencies/useCases/useGetCurrenciesCatalogs'
import { CurrencyCatalogsDTO } from '@/domain/currencies/currencyTypes'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { normalizeText } from '@/utils/textUtils'
import useOffsets from '@/hooks/useOffsets'

export const ChoosePaymentCurrencyScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()

  const [searchValue, setSearchValue] = useState('')
  const paymentCurrencyOnPreferences = useAppPreferencesStore(
    (s) => s.paymentCurrency,
  )
  const setPaymentCurrency = useAppPreferencesStore((s) => s.setPaymentCurrency)

  const [selectedPaymentCurrency, setSelectedPaymentCurrency] = useState(
    paymentCurrencyOnPreferences,
  )

  const { data: displayCurrencies, isLoading } = useGetCurrenciesCatalogs({
    is_available: true,
    is_payment_currency: true,
  })

  const availableDisplayCurrencies: CurrencyCatalogsDTO[] =
    (displayCurrencies as any)?.currencies || []

  const sortedDisplayCurrencies = [...availableDisplayCurrencies].sort(
    (a, b) => {
      if (a.isin === paymentCurrencyOnPreferences?.isin) return -1
      if (b.isin === paymentCurrencyOnPreferences?.isin) return 1
      return 0
    },
  )

  const filteredDisplayCurrencies = useMemo(() => {
    const normalizedSearch = normalizeText(searchValue)

    return sortedDisplayCurrencies?.filter(({ name, isin }) => {
      const normalizedName = normalizeText(name)
      const normalizedIsin = normalizeText(isin)

      return (
        normalizedName.includes(normalizedSearch) ||
        normalizedIsin.includes(normalizedSearch)
      )
    })
  }, [searchValue, sortedDisplayCurrencies])

  const handleSavePaymentCurrency = () => {
    if (selectedPaymentCurrency) {
      setPaymentCurrency(selectedPaymentCurrency)

      // Show successful update as in RN version
      navigate('/successful-profile-update', {
        state: {
          message: t('common:paymentCurrencyUpdated'),
          description: `${t('common:selectedPaymentCurrency')} ${selectedPaymentCurrency.isin}`,
          doneRoute: '/profile/profile-settings',
          navigationMethod: 'dismiss',
        },
      })
    }
  }

  return (
    <BaseView
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 32,
        paddingBottom: `${offsets.bottom + 100}px`,
      }}
    >
      {/* Header Navigation with Title and Description */}
      <HeaderWithTitleAndDescription
        title={t('bottomSheets:selectPaymentCurrency.title')}
        description={t(
          'screens:profile.profileSettings.items.paymentCurrency.description',
        )}
        onBackPress={() => navigate(-1)}
      >
        {/* Search Input in header */}
        <BaseView mt={10}>
          <SearchInput value={searchValue} onChangeText={setSearchValue} />
        </BaseView>
      </HeaderWithTitleAndDescription>

      {/* Content */}
      <BaseView
        style={{
          flex: 1,
          paddingLeft: '13px',
          paddingRight: '13px',
          paddingTop: `${offsets.top + 220}px`, // top offset + navigation height + title + gap + description + gap + padding + search input + margin
        }}
      >
        {/* Currency List */}
        <BaseView gap={12}>
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 5 }).map((_, index) => (
              <SelectCardItem
                key={index}
                title=""
                description=""
                isSelected={false}
                skeletonActive={true}
              />
            ))
          ) : filteredDisplayCurrencies.length === 0 ? (
            // No results found
            <BaseView center py={40}>
              <BaseText fontSize={16} color="label.disabled" textAlign="center">
                {searchValue
                  ? t('common:noSearchResults')
                  : t('screens:conversion.noCurrenciesAvailable')}
              </BaseText>
              {searchValue && (
                <BaseView mt={8}>
                  <BaseText
                    fontSize={14}
                    color="label.disabled"
                    textAlign="center"
                  >
                    {t('common:tryDifferentSearch')}
                  </BaseText>
                </BaseView>
              )}
            </BaseView>
          ) : (
            // Currency list
            filteredDisplayCurrencies.map((currency) => {
              const isSelected = selectedPaymentCurrency?.isin === currency.isin

              return (
                <SelectCardItem
                  key={currency.isin}
                  isSelected={isSelected}
                  title={currency.name}
                  description={currency.isin}
                  imagePath={currency.img_url}
                  onPress={() => setSelectedPaymentCurrency(currency)}
                />
              )
            })
          )}
        </BaseView>
      </BaseView>

      {/* Fixed Bottom Button */}
      <FixedBottomButton
        buttons={{
          label: t('form:actions.save'),
          disabled:
            selectedPaymentCurrency?.isin ===
            paymentCurrencyOnPreferences?.isin,
          onPress: handleSavePaymentCurrency,
          type: 'primaryGradient',
        }}
      />
    </BaseView>
  )
}
